import { Injectable } from '@angular/core';

/**
 * DOVE-2676 — a change the user confirmed but that hasn't been sent yet (e.g. a bulk access
 * removal still inside its undo countdown). Nothing is sent if the user leaves, so leaving is
 * stopped first: in-app navigation goes through PendingChangesGuard, which calls confirmLeave();
 * refresh / close / a typed URL get the browser's own "Leave site?" prompt via beforeunload.
 */
export interface PendingChange {
	/** Shows the owner's own "leave without saving?" prompt; resolves true when the user leaves. */
	confirmLeave(): Promise<boolean>;
	/** Drops the change without sending it (system navigations: logout, session expiry, …). */
	discard(): void;
}

@Injectable({ providedIn: 'root' })
export class PendingChangesService {
	private readonly pending = new Set<PendingChange>();

	constructor() {
		window.addEventListener('beforeunload', (event) => this.onBeforeUnload(event));
	}

	get hasPending(): boolean {
		return this.pending.size > 0;
	}

	/** Returns the function that releases the change once it's sent, undone or discarded. */
	register(change: PendingChange): () => void {
		this.pending.add(change);
		return () => this.pending.delete(change);
	}

	/** Asks each pending change in turn; true only when the user agrees to leave all of them. */
	async confirmLeave(): Promise<boolean> {
		for (const change of [...this.pending]) {
			if (!(await change.confirmLeave())) return false;
		}
		return true;
	}

	/** Refresh, close or a typed URL while something is pending: the browser shows its own prompt. */
	onBeforeUnload(event: Pick<BeforeUnloadEvent, 'preventDefault' | 'returnValue'>): void {
		if (!this.pending.size) return;
		// Its text can't be customized.
		event.preventDefault();
		event.returnValue = '';
	}

	/**
	 * For navigations the user can't be asked to stay on (logout, session expiry, maintenance,
	 * no access): drop every unsent change, so neither the guard nor the browser prompt gets in the way.
	 */
	discardAll(): void {
		const changes = [...this.pending];
		this.pending.clear();
		changes.forEach((change) => change.discard());
	}
}
